import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  MapPin, Truck, DollarSign, Calendar, Search,
  ChevronLeft, ChevronRight, RefreshCw, Filter,
  ArrowRight, Building2, Phone, Star, AlertTriangle,
  Package, Clock, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const STATUS_COLORS = {
  LISTED:    'bg-green-500/15 text-green-400 border-green-500/30',
  EXPIRED:   'bg-red-500/15 text-red-400 border-red-500/30',
  CANCELLED: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  PENDING:   'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
};

const TRAILER_COLORS = {
  OPEN:     'bg-blue-500/15 text-blue-400 border-blue-500/30',
  ENCLOSED: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
};

function Badge({ label, colorClass }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
      {label}
    </span>
  );
}

function fmt(date) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ListingCard({ item }) {
  const trailerColor = TRAILER_COLORS[item.trailer_type] || TRAILER_COLORS.OPEN;
  const statusColor  = STATUS_COLORS[item.status] || STATUS_COLORS.LISTED;

  return (
    <div className="bg-card border border-border rounded-xl p-5 hover:border-accent/40 transition-colors">
      {/* Top row: price + badges */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-2xl font-bold text-foreground font-outfit">
            ${item.price != null ? item.price.toLocaleString() : '—'}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {item.shipper_order_id || 'No order ID'}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end">
          <Badge label={item.trailer_type || 'OPEN'} colorClass={trailerColor} />
          <Badge label={item.status || 'LISTED'}    colorClass={statusColor} />
          {item.has_inop && (
            <Badge label="INOP" colorClass="bg-orange-500/15 text-orange-400 border-orange-500/30" />
          )}
          {item.requires_inspection && (
            <Badge label="INSPECTION" colorClass="bg-yellow-500/15 text-yellow-400 border-yellow-500/30" />
          )}
        </div>
      </div>

      {/* Route */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-muted-foreground mb-0.5">
            <MapPin className="h-3 w-3 text-green-400 shrink-0" />
            <span className="text-xs uppercase tracking-wide">Origin</span>
          </div>
          <div className="font-semibold text-foreground text-sm truncate">
            {item.origin_city}, {item.origin_state}
          </div>
          <div className="text-xs text-muted-foreground">{item.origin_zip}</div>
        </div>

        <div className="flex flex-col items-center gap-0.5 shrink-0">
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {item.distance_miles ? `${item.distance_miles.toLocaleString()} mi` : '—'}
          </span>
        </div>

        <div className="flex-1 min-w-0 text-right">
          <div className="flex items-center justify-end gap-1 text-muted-foreground mb-0.5">
            <span className="text-xs uppercase tracking-wide">Destination</span>
            <MapPin className="h-3 w-3 text-red-400 shrink-0" />
          </div>
          <div className="font-semibold text-foreground text-sm truncate">
            {item.dest_city}, {item.dest_state}
          </div>
          <div className="text-xs text-muted-foreground">{item.dest_zip}</div>
        </div>
      </div>

      {/* Vehicle */}
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
        <Package className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm text-foreground">
          {item.vehicle_year} {item.vehicle_make} {item.vehicle_model}
        </span>
        <span className="text-xs text-muted-foreground ml-auto">{item.vehicle_type}</span>
      </div>

      {/* Shipper + dates */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="font-medium text-foreground truncate">{item.shipper_name || '—'}</span>
          </div>
          {item.shipper_phone && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Phone className="h-3 w-3 shrink-0" />
              <span>{item.shipper_phone}</span>
            </div>
          )}
          {item.shipper_rating != null && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Star className="h-3 w-3 text-yellow-400 shrink-0" />
              <span>{item.shipper_rating.toFixed(1)}%</span>
              {item.shipper_tier && (
                <span className="ml-1 uppercase text-[10px] text-muted-foreground/70">{item.shipper_tier}</span>
              )}
            </div>
          )}
        </div>

        <div className="space-y-1.5 text-right">
          <div className="flex items-center justify-end gap-1.5 text-muted-foreground">
            <Calendar className="h-3 w-3 shrink-0" />
            <span>Available {fmt(item.available_date)}</span>
          </div>
          <div className="flex items-center justify-end gap-1.5 text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" />
            <span>Expires {fmt(item.expiration_date)}</span>
          </div>
        </div>
      </div>

      {item.pre_dispatch_notes && (
        <div className="mt-3 text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2 italic">
          {item.pre_dispatch_notes}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, children }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent transition-colors"
      >
        {children}
      </select>
    </div>
  );
}

function FilterInput({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors"
      />
    </div>
  );
}

export default function CDListingsPage() {
  const { token } = useAuth();

  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [pages,   setPages]   = useState(1);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);

  const [originState,  setOriginState]  = useState('');
  const [destState,    setDestState]    = useState('');
  const [trailerType,  setTrailerType]  = useState('');
  const [status,       setStatus]       = useState('LISTED');
  const [minPrice,     setMinPrice]     = useState('');
  const [maxPrice,     setMaxPrice]     = useState('');
  const [search,       setSearch]       = useState('');

  const fetchListings = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 25 });
      if (originState)  params.set('origin_state',      originState);
      if (destState)    params.set('destination_state', destState);
      if (trailerType)  params.set('trailer_type',      trailerType);
      if (status)       params.set('status',            status);
      if (minPrice)     params.set('min_price',         minPrice);
      if (maxPrice)     params.set('max_price',         maxPrice);
      if (search)       params.set('search',            search);

      const res = await fetch(`${API_URL}/api/cd-listings?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setItems(data.items);
      setTotal(data.total);
      setPages(data.pages);
      setPage(pg);
    } catch (err) {
      toast.error('Failed to load listings: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [token, originState, destState, trailerType, status, minPrice, maxPrice, search]);

  useEffect(() => { fetchListings(1); }, []);   // initial load

  const applyFilters = () => fetchListings(1);
  const clearFilters = () => {
    setOriginState(''); setDestState(''); setTrailerType('');
    setStatus('LISTED'); setMinPrice(''); setMaxPrice(''); setSearch('');
    setTimeout(() => fetchListings(1), 0);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground font-outfit">CD Listings</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {total.toLocaleString()} listings in database
            </p>
          </div>
          <button
            onClick={() => fetchListings(page)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground hover:border-accent/50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="flex gap-6">

          {/* ── Filters sidebar ── */}
          <aside className="w-64 shrink-0 space-y-4">
            <div className="bg-card border border-border rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground text-sm">Filters</span>
                </div>
                <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                  <X className="h-3 w-3" /> Clear
                </button>
              </div>

              <FilterInput
                label="Search"
                value={search}
                onChange={setSearch}
                placeholder="Order ID or company…"
              />

              <FilterSelect label="Origin State" value={originState} onChange={setOriginState}>
                <option value="">All States</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </FilterSelect>

              <FilterSelect label="Destination State" value={destState} onChange={setDestState}>
                <option value="">All States</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </FilterSelect>

              <FilterSelect label="Trailer Type" value={trailerType} onChange={setTrailerType}>
                <option value="">All Types</option>
                <option value="OPEN">Open</option>
                <option value="ENCLOSED">Enclosed</option>
              </FilterSelect>

              <FilterSelect label="Status" value={status} onChange={setStatus}>
                <option value="">All</option>
                <option value="LISTED">Listed</option>
                <option value="EXPIRED">Expired</option>
                <option value="CANCELLED">Cancelled</option>
              </FilterSelect>

              <div className="grid grid-cols-2 gap-2">
                <FilterInput label="Min $" value={minPrice} onChange={setMinPrice} placeholder="0" type="number" />
                <FilterInput label="Max $" value={maxPrice} onChange={setMaxPrice} placeholder="9999" type="number" />
              </div>

              <button
                onClick={applyFilters}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
              >
                <Search className="h-4 w-4" />
                Apply Filters
              </button>
            </div>
          </aside>

          {/* ── Listings grid ── */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-24 text-muted-foreground">
                <RefreshCw className="h-6 w-6 animate-spin mr-3" />
                Loading listings…
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
                <Truck className="h-12 w-12 mb-4 opacity-30" />
                <p className="text-lg font-medium">No listings found</p>
                <p className="text-sm mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
                  {items.map(item => (
                    <ListingCard key={item.id} item={item} />
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {pages} &nbsp;·&nbsp; {total.toLocaleString()} total
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fetchListings(page - 1)}
                      disabled={page <= 1 || loading}
                      className="flex items-center gap-1 px-3 py-1.5 bg-card border border-border rounded-lg text-sm text-foreground hover:border-accent/50 disabled:opacity-40 transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" /> Prev
                    </button>
                    <button
                      onClick={() => fetchListings(page + 1)}
                      disabled={page >= pages || loading}
                      className="flex items-center gap-1 px-3 py-1.5 bg-card border border-border rounded-lg text-sm text-foreground hover:border-accent/50 disabled:opacity-40 transition-colors"
                    >
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
